import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Badge, Card, CardBody, Table } from "reactstrap";

import { Order, OrderItem } from "../../../../services/orderService";

interface OrderCommissionDetailsCardProps {
  order: Order;
  formatMoney: (cents: number) => string;
}

const resolveItemDescription = (item: OrderItem): string => {
  if (item.service?.name) {
    return item.service.name;
  }

  if (item.product?.name) {
    return item.product.name;
  }

  if (item.service_package?.name) {
    return item.service_package.name;
  }

  return "—";
};

const OrderCommissionDetailsCard = ({
  order,
  formatMoney,
}: OrderCommissionDetailsCardProps) => {
  const { t } = useTranslation();

  if (order.status !== "paid") {
    return null;
  }

  const commissionedItems = (order.order_items ?? []).filter(
    (item) => item.commission_amount !== null
  );

  if (commissionedItems.length === 0) {
    return null;
  }

  const totalCommission = commissionedItems.reduce(
    (sum, item) => sum + (item.commission_amount ?? 0),
    0
  );

  return (
    <Card className="border shadow-none mb-4">
      <CardBody className="py-3">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <h6 className="mb-0">{t("studio.orders.commission.sectionTitle")}</h6>
          <span className="fw-medium">
            {t("studio.orders.commission.totalLabel", {
              total: formatMoney(totalCommission),
            })}
          </span>
        </div>
        <div className="table-responsive">
          <Table size="sm" className="align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>{t("studio.orders.table.description")}</th>
                <th>{t("studio.orders.table.professional")}</th>
                <th>{t("studio.orders.commission.rateLabel")}</th>
                <th>{t("studio.orders.commission.amountLabel")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {commissionedItems.map((item) => {
                const defaultBps = item.professional?.commission_rate_bps;
                const rateBps =
                  item.commission_rate_bps ?? defaultBps ?? null;
                const isSpecial =
                  item.commission_rate_bps != null &&
                  defaultBps != null &&
                  item.commission_rate_bps !== defaultBps;
                const rateLabel =
                  rateBps !== null && rateBps !== undefined
                    ? `${(rateBps / 100).toFixed(2)}%`
                    : t("studio.orders.common.notAvailable");

                return (
                  <tr key={item.id}>
                    <td>
                      {resolveItemDescription(item)}
                      {item.item_type === "retail" ? (
                        <Badge color="secondary" className="badge-soft-secondary ms-2">
                          {t("studio.orders.commission.productBadge")}
                        </Badge>
                      ) : null}
                      {isSpecial ? (
                        <Badge color="info" className="badge-soft-info ms-2">
                          {t("studio.orders.commission.specialRateBadge")}
                        </Badge>
                      ) : null}
                    </td>
                    <td>{item.professional?.name ?? t("studio.orders.common.notAvailable")}</td>
                    <td>{rateLabel}</td>
                    <td>{formatMoney(item.commission_amount ?? 0)}</td>
                    <td className="text-end">
                      {item.professional_id ? (
                        <Link
                          to={`/professionals?open=${item.professional_id}&tab=commissions`}
                          className="small"
                        >
                          {t("studio.orders.commission.viewProfessional")}
                        </Link>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      </CardBody>
    </Card>
  );
};

export default OrderCommissionDetailsCard;
